import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { 
  format, 
  startOfDay, 
  endOfDay, 
  startOfWeek, 
  endOfWeek, 
  subWeeks,
  isAfter 
} from "date-fns";

interface AppointmentType {
  id: string;
  title: string;
  date: string;
  end_date?: string;
  type: string;
  location?: string;
  notes?: string;
  client_id?: string;
}

interface ClientType {
  name: string;
}

interface AppointmentWithClient extends AppointmentType {
  clients?: ClientType | null;
}

interface DashboardStats {
  totalClients: number;
  todayAppointments: number;
  weekAppointments: number;
  totalActiveAppointments: number;
  upcomingAppointments: AppointmentWithClient[];
  appointmentsByType: Array<{ type: string; count: number }>;
  weeklyStats: Array<{ week: string; count: number }>;
}

export const useDashboardStats = () => {
  const { data: stats, isLoading, error, refetch } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async (): Promise<DashboardStats> => {
      try {
        // Verificar autenticação
        const { data: userData, error: authError } = await supabase.auth.getUser();
        if (authError || !userData?.user) {
          throw new Error("Usuário não autenticado");
        }

        const userId = userData.user.id;
        const today = new Date();
        const startToday = startOfDay(today);
        const endToday = endOfDay(today);
        const startWeek = startOfWeek(today);
        const endWeek = endOfWeek(today);

        // Executar todas as queries em paralelo para melhor performance
        const [
          clientsResult,
          todayAppointmentsResult,
          weekAppointmentsResult,
          totalActiveAppointmentsResult,
          appointmentsForTypesResult,
        ] = await Promise.all([
          // Total de clientes
          supabase
            .from("clients")
            .select("*", { count: 'exact', head: true })
            .eq("user_id", userId),

          // Compromissos de hoje
          supabase
            .from("appointments")
            .select("*", { count: 'exact', head: true })
            .eq("user_id", userId)
            .eq("archived", false)
            .gte("date", startToday.toISOString())
            .lte("date", endToday.toISOString()),

          // Compromissos da semana
          supabase
            .from("appointments")
            .select("*", { count: 'exact', head: true })
            .eq("user_id", userId)
            .eq("archived", false)
            .gte("date", startWeek.toISOString())
            .lte("date", endWeek.toISOString()),

          // Total de compromissos ativos
          supabase
            .from("appointments")
            .select("*", { count: 'exact', head: true })
            .eq("user_id", userId)
            .eq("archived", false),

          // Todos os compromissos para análise de tipos
          supabase
            .from("appointments")
            .select("type")
            .eq("user_id", userId)
            .eq("archived", false)
        ]);

        // Verificar erros nas queries básicas
        if (clientsResult.error) throw clientsResult.error;
        if (todayAppointmentsResult.error) throw todayAppointmentsResult.error;
        if (weekAppointmentsResult.error) throw weekAppointmentsResult.error;
        if (totalActiveAppointmentsResult.error) throw totalActiveAppointmentsResult.error;
        if (appointmentsForTypesResult.error) throw appointmentsForTypesResult.error;

        // Buscar próximos compromissos com informações completas
        let upcomingAppointments: AppointmentWithClient[] = [];
        
        try {
          const { data: appointmentsData, error: appointmentsError } = await supabase
            .from("appointments")
            .select(`
              id,
              title,
              date,
              end_date,
              type,
              location,
              notes,
              client_id
            `)
            .eq("user_id", userId)
            .eq("archived", false)
            .gte("date", today.toISOString())
            .order("date", { ascending: true })
            .limit(10); // Buscar um pouco mais para ter margem

          if (appointmentsError) {
            console.warn("Erro ao buscar próximos compromissos:", appointmentsError);
          } else if (appointmentsData && Array.isArray(appointmentsData)) {
            // Buscar informações dos clientes em batch
            const clientIds = appointmentsData
              .map(apt => apt.client_id)
              .filter((id): id is string => Boolean(id));

            let clientsMap: Record<string, ClientType> = {};
            
            if (clientIds.length > 0) {
              const { data: clientsData, error: clientsError } = await supabase
                .from("clients")
                .select("id, name")
                .in("id", clientIds);

              if (clientsError) {
                console.warn("Erro ao buscar dados dos clientes:", clientsError);
              } else if (clientsData) {
                clientsMap = clientsData.reduce((acc, client) => {
                  acc[client.id] = { name: client.name };
                  return acc;
                }, {} as Record<string, ClientType>);
              }
            }

            // Combinar appointments com dados dos clientes
            upcomingAppointments = appointmentsData
              .map(appointment => ({
                ...appointment,
                clients: appointment.client_id ? clientsMap[appointment.client_id] || null : null
              }))
              .filter(appointment => isAfter(new Date(appointment.date), today))
              .slice(0, 5); // Limitar a 5 próximos
          }
        } catch (upcomingError) {
          console.error("Erro ao processar próximos compromissos:", upcomingError);
          upcomingAppointments = [];
        }

        // Processar distribuição por tipo
        const typeStats = appointmentsForTypesResult.data?.reduce((acc: Record<string, number>, curr) => {
          const type = curr.type || 'Outro';
          acc[type] = (acc[type] || 0) + 1;
          return acc;
        }, {}) || {};

        // Garantir que todos os tipos apareçam
        const allTypes = ["Audiência", "Reunião", "Prazo", "Despacho", "Outro"];
        const appointmentsByType = allTypes.map(type => ({
          type,
          count: typeStats[type] || 0
        }));

        // Estatísticas semanais (últimas 4 semanas)
        const weeklyStatsPromises = [];
        for (let i = 3; i >= 0; i--) {
          const weekStart = startOfWeek(subWeeks(today, i));
          const weekEnd = endOfWeek(subWeeks(today, i));
          
          weeklyStatsPromises.push(
            supabase
              .from("appointments")
              .select("*", { count: 'exact', head: true })
              .eq("user_id", userId)
              .eq("archived", false)
              .gte("date", weekStart.toISOString())
              .lte("date", weekEnd.toISOString())
              .then(result => ({
                week: `Sem ${4 - i}`,
                count: result.count || 0,
                error: result.error
              }))
          );
        }

        const weeklyResults = await Promise.all(weeklyStatsPromises);
        
        // Verificar erros nas queries semanais
        const weeklyErrors = weeklyResults.filter(result => result.error);
        if (weeklyErrors.length > 0) {
          console.warn("Erros nas estatísticas semanais:", weeklyErrors);
        }

        const weeklyStats = weeklyResults.map(({ week, count }) => ({ week, count }));

        // Retornar dados consolidados
        const finalStats: DashboardStats = {
          totalClients: clientsResult.count || 0,
          todayAppointments: todayAppointmentsResult.count || 0,
          weekAppointments: weekAppointmentsResult.count || 0,
          totalActiveAppointments: totalActiveAppointmentsResult.count || 0,
          upcomingAppointments,
          appointmentsByType,
          weeklyStats
        };

        // console.log("Dashboard stats carregadas com sucesso:", {
        //   totalClients: finalStats.totalClients,
        //   totalActive: finalStats.totalActiveAppointments,
        //   upcomingCount: finalStats.upcomingAppointments.length,
        //   typesWithData: finalStats.appointmentsByType.filter(t => t.count > 0).length
        // });

        return finalStats;

      } catch (error) {
        console.error("Erro ao carregar estatísticas do dashboard:", error);
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
    cacheTime: 10 * 60 * 1000, // 10 minutos
    retry: 2,
    refetchOnWindowFocus: false,
  });

  return { 
    stats, 
    isLoading, 
    error, 
    refetch,
    // Funções auxiliares para componentes
    hasData: Boolean(stats?.totalActiveAppointments && stats.totalActiveAppointments > 0),
    hasUpcoming: Boolean(stats?.upcomingAppointments && stats.upcomingAppointments.length > 0)
  };
};